import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { RefreshCw, Copy, Send, AlertTriangle, Coffee } from "lucide-react";
import type { FleetCapacityByType } from "./utils";
import { generateBreakSuggestions, type BreakSuggestion } from "./breakSchedulerUtils";

interface BreaksTabProps {
  dataByType: Record<string, FleetCapacityByType>;
  dateStr: string;
  /** shift_ids whose published break is blocked by a real booking with no room left to push it -- computed at the page level, see useBreakConflictAutoPush. */
  stuckShiftIds: Set<string>;
}

type SavedBreakRow = {
  id: string;
  shift_id: string;
  driver_name: string;
  vehicle_number: string | null;
  suggested_start_hhmm: string;
  suggested_end_hhmm: string;
  confirmed_start_hhmm: string | null;
  confirmed_end_hhmm: string | null;
  status: "draft" | "published";
  taken: boolean;
};

const DEFAULT_MAX_HOURS = 7;
const DEFAULT_BREAK_MINUTES = 45;
const DEFAULT_ALLOW_OVERLAP = false;
const DEFAULT_OVERLAP_PERCENT = 50;

function addMinutesToHHMM(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  const total = (h * 60 + m + minutes + 24 * 60) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

const BreaksTab: React.FC<BreaksTabProps> = ({ dataByType, dateStr, stuckShiftIds }) => {
  const [maxHours, setMaxHours] = useState(DEFAULT_MAX_HOURS);
  const [breakMinutes, setBreakMinutes] = useState(DEFAULT_BREAK_MINUTES);
  const [allowOverlap, setAllowOverlap] = useState(DEFAULT_ALLOW_OVERLAP);
  const [overlapPercent, setOverlapPercent] = useState(DEFAULT_OVERLAP_PERCENT);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [savedBreaks, setSavedBreaks] = useState<Record<string, SavedBreakRow>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase
      .from("settings")
      .select("name, value")
      .in("name", ["fleet_break_max_hours", "fleet_break_minutes", "fleet_break_allow_overlap", "fleet_break_overlap_percent"])
      .then(({ data }) => {
        const byName = new Map((data || []).map((s) => [s.name, s.value]));
        const h = parseInt(String(byName.get("fleet_break_max_hours") ?? ""), 10);
        const m = parseInt(String(byName.get("fleet_break_minutes") ?? ""), 10);
        const p = parseInt(String(byName.get("fleet_break_overlap_percent") ?? ""), 10);
        if (!Number.isNaN(h) && h > 0) setMaxHours(h);
        if (!Number.isNaN(m) && m > 0) setBreakMinutes(m);
        if (byName.has("fleet_break_allow_overlap")) setAllowOverlap(byName.get("fleet_break_allow_overlap") === "true");
        if (!Number.isNaN(p) && p >= 0) setOverlapPercent(p);
        setSettingsLoaded(true);
      });
  }, []);

  const saveSetting = useCallback((name: string, value: string) => {
    supabase.from("settings").upsert({ name, value, category: "Fleet", is_active: true }, { onConflict: "name" });
  }, []);

  const loadSavedBreaks = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("roster_scheduled_breaks")
      .select("id, shift_id, driver_name, vehicle_number, suggested_start_hhmm, suggested_end_hhmm, confirmed_start_hhmm, confirmed_end_hhmm, status, taken")
      .eq("break_date", dateStr);
    setLoading(false);
    if (error) {
      toast({ title: "Error loading breaks", description: error.message, variant: "destructive" });
      return;
    }
    const byShiftId: Record<string, SavedBreakRow> = {};
    (data || []).forEach((row) => {
      byShiftId[row.shift_id] = row as SavedBreakRow;
    });
    setSavedBreaks(byShiftId);
  }, [dateStr]);

  useEffect(() => {
    loadSavedBreaks();
  }, [loadSavedBreaks]);

  const liveSuggestions: BreakSuggestion[] = useMemo(() => {
    if (!settingsLoaded) return [];
    return generateBreakSuggestions(dataByType, dateStr, maxHours, breakMinutes, allowOverlap, overlapPercent);
  }, [dataByType, dateStr, maxHours, breakMinutes, allowOverlap, overlapPercent, settingsLoaded]);

  const hasSavedForToday = Object.keys(savedBreaks).length > 0;

  type Row = {
    id: string | null;
    shiftId: string;
    driverName: string;
    vehicleNumber: string | null;
    startHHMM: string | null;
    endHHMM: string | null;
    status: "draft" | "published" | "unsaved";
    needsReview: boolean;
    taken: boolean;
  };

  const rows: Row[] = useMemo(() => {
    return liveSuggestions.map((s) => {
      const saved = savedBreaks[s.shiftId];
      if (saved) {
        const start = saved.confirmed_start_hhmm || saved.suggested_start_hhmm || null;
        const end = saved.confirmed_end_hhmm || saved.suggested_end_hhmm || null;
        return {
          id: saved.id,
          shiftId: s.shiftId,
          driverName: s.driverName,
          vehicleNumber: s.vehicleNumber,
          startHHMM: start,
          endHHMM: end,
          status: saved.status,
          needsReview: !start,
          taken: saved.taken,
        };
      }
      return {
        id: null,
        shiftId: s.shiftId,
        driverName: s.driverName,
        vehicleNumber: s.vehicleNumber,
        startHHMM: s.suggestedStartHHMM,
        endHHMM: s.suggestedEndHHMM,
        status: "unsaved" as const,
        needsReview: !s.suggestedStartHHMM,
        taken: false,
      };
    });
  }, [liveSuggestions, savedBreaks]);

  const handleGenerate = async () => {
    setSaving(true);
    try {
      await supabase.from("roster_scheduled_breaks").delete().eq("break_date", dateStr).eq("status", "draft");

      const inserts = liveSuggestions
        .filter((s) => !savedBreaks[s.shiftId] || savedBreaks[s.shiftId].status === "draft")
        .map((s) => ({
          shift_id: s.shiftId,
          driver_name: s.driverName,
          vehicle_number: s.vehicleNumber,
          break_date: dateStr,
          suggested_start_hhmm: s.suggestedStartHHMM || s.shiftStartHHMM,
          suggested_end_hhmm: s.suggestedEndHHMM || addMinutesToHHMM(s.shiftStartHHMM, breakMinutes),
          status: "draft" as const,
        }));

      if (inserts.length > 0) {
        const { error } = await supabase.from("roster_scheduled_breaks").insert(inserts);
        if (error) throw error;
      }

      toast({ title: "Generated", description: `${inserts.length} break suggestions saved as draft.` });
      loadSavedBreaks();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to generate breaks";
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustTime = async (shiftId: string, newStart: string) => {
    const newEnd = addMinutesToHHMM(newStart, breakMinutes);
    const existing = savedBreaks[shiftId];

    if (existing) {
      const { error } = await supabase
        .from("roster_scheduled_breaks")
        .update({ confirmed_start_hhmm: newStart, confirmed_end_hhmm: newEnd, updated_at: new Date().toISOString() })
        .eq("id", existing.id);
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        return;
      }
    } else {
      const suggestion = liveSuggestions.find((s) => s.shiftId === shiftId);
      if (!suggestion) return;
      const { error } = await supabase.from("roster_scheduled_breaks").insert({
        shift_id: shiftId,
        driver_name: suggestion.driverName,
        vehicle_number: suggestion.vehicleNumber,
        break_date: dateStr,
        suggested_start_hhmm: suggestion.suggestedStartHHMM || suggestion.shiftStartHHMM,
        suggested_end_hhmm: suggestion.suggestedEndHHMM || addMinutesToHHMM(suggestion.shiftStartHHMM, breakMinutes),
        confirmed_start_hhmm: newStart,
        confirmed_end_hhmm: newEnd,
        status: "draft",
      });
      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
        return;
      }
    }
    loadSavedBreaks();
  };

  // "Taken" locks the break out of the auto-push conflict check (see
  // useBreakConflictAutoPush) -- once a dispatcher confirms it actually
  // happened, even a few minutes early/late, a nearby booking shouldn't
  // bounce it around anymore.
  const handleToggleTaken = async (id: string, taken: boolean) => {
    const { error } = await supabase
      .from("roster_scheduled_breaks")
      .update({ taken, updated_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    loadSavedBreaks();
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("roster_scheduled_breaks")
        .update({ status: "published", published_at: new Date().toISOString() })
        .eq("break_date", dateStr)
        .eq("status", "draft");
      if (error) throw error;
      toast({ title: "Published", description: "Break schedule is now visible to drivers." });
      loadSavedBreaks();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to publish";
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = () => {
    const publishedRows = rows
      .filter((r) => r.status === "published" && r.startHHMM && r.endHHMM)
      .sort((a, b) => (a.startHHMM || "").localeCompare(b.startHHMM || ""));

    if (publishedRows.length === 0) {
      toast({ title: "Nothing to copy", description: "No published breaks for this day yet." });
      return;
    }

    const text = publishedRows
      .map((r) => `${r.vehicleNumber || "???"} - ${r.driverName} (break ${r.startHHMM}-${r.endHHMM})`)
      .join("\n");

    navigator.clipboard.writeText(text).then(() => {
      toast({ title: "Copied", description: "Break schedule copied to clipboard" });
    });
  };

  const publishedCount = rows.filter((r) => r.status === "published").length;
  const draftCount = rows.filter((r) => r.status === "draft" || r.status === "unsaved").length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <Coffee className="h-4 w-4 text-yellow-400" />
          <span className="font-semibold">Scheduled Breaks</span>
          <span className="text-gray-500">-- {rows.length} driving shift{rows.length === 1 ? "" : "s"} today</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap text-xs">
          <label className="flex items-center gap-1.5 text-gray-400">
            Max hours before break
            <Input
              type="number"
              min={1}
              value={maxHours}
              onChange={(e) => {
                const v = Math.max(1, Number(e.target.value) || 1);
                setMaxHours(v);
                saveSetting("fleet_break_max_hours", String(v));
              }}
              className="h-7 w-16 bg-gray-800 border-gray-600 text-white text-xs"
            />
          </label>
          <label className="flex items-center gap-1.5 text-gray-400">
            Break length (min)
            <Input
              type="number"
              min={5}
              step={5}
              value={breakMinutes}
              onChange={(e) => {
                const v = Math.max(5, Number(e.target.value) || 5);
                setBreakMinutes(v);
                saveSetting("fleet_break_minutes", String(v));
              }}
              className="h-7 w-16 bg-gray-800 border-gray-600 text-white text-xs"
            />
          </label>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap text-xs bg-gray-800/40 border border-gray-700 rounded-md px-3 py-2">
        <label className="flex items-center gap-1.5 text-gray-300 cursor-pointer">
          <Checkbox
            checked={allowOverlap}
            onCheckedChange={(checked) => {
              const v = !!checked;
              setAllowOverlap(v);
              saveSetting("fleet_break_allow_overlap", String(v));
            }}
          />
          Can breaks overlap?
        </label>

        {allowOverlap && (
          <>
            <label className="flex items-center gap-1.5 text-gray-400">
              Overlap by
              <Input
                type="number"
                min={0}
                max={100}
                value={overlapPercent}
                onChange={(e) => {
                  const v = Math.min(100, Math.max(0, Number(e.target.value) || 0));
                  setOverlapPercent(v);
                  saveSetting("fleet_break_overlap_percent", String(v));
                }}
                className="h-7 w-16 bg-gray-800 border-gray-600 text-white text-xs"
              />
              %
            </label>
            <span className="text-gray-500">
              -- a {breakMinutes} min break can overlap another driver's by up to {Math.round((breakMinutes * overlapPercent) / 100)} min
            </span>
          </>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={handleGenerate} disabled={saving || loading} className="bg-blue-600 hover:bg-blue-700 text-white">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${saving ? "animate-spin" : ""}`} />
          {hasSavedForToday ? "Regenerate Draft Suggestions" : "Generate Suggested Breaks"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handlePublish}
          disabled={saving || draftCount === 0}
          className="border-green-700 text-green-400 hover:bg-green-900/30"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          Publish {draftCount > 0 ? `${draftCount} Draft${draftCount === 1 ? "" : "s"}` : ""}
        </Button>
        <Button size="sm" variant="outline" onClick={handleCopy} className="text-gray-300">
          <Copy className="h-3.5 w-3.5 mr-1.5" />
          Copy Schedule ({publishedCount} published)
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400">No driving shifts rostered for this day need a break.</p>
      ) : (
        <div className="rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-800/80 text-gray-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Driver</th>
                <th className="text-left px-3 py-2">Vehicle</th>
                <th className="text-left px-3 py-2">Shift</th>
                <th className="text-left px-3 py-2">Break Time</th>
                <th className="text-left px-3 py-2">Had Break</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const suggestion = liveSuggestions.find((s) => s.shiftId === r.shiftId);
                return (
                  <tr key={r.shiftId} className="border-t border-gray-700/60 hover:bg-gray-800/30">
                    <td className="px-3 py-2 text-white font-medium">{r.driverName}</td>
                    <td className="px-3 py-2 text-gray-300">{r.vehicleNumber || "???"}</td>
                    <td className="px-3 py-2 text-gray-400">
                      {suggestion?.shiftStartHHMM}-{suggestion?.shiftEndHHMM}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {r.needsReview && <AlertTriangle className="h-3.5 w-3.5 text-orange-400 shrink-0" />}
                        <Input
                          type="time"
                          value={r.startHHMM || ""}
                          onChange={(e) => handleAdjustTime(r.shiftId, e.target.value)}
                          className="h-8 w-28 bg-gray-800 border-gray-600 text-white text-xs"
                        />
                        {r.startHHMM && r.endHHMM && (
                          <span className="text-xs text-gray-500">- {r.endHHMM}</span>
                        )}
                      </div>
                      {r.needsReview && (
                        <p className="text-[11px] text-orange-400 mt-1">{suggestion?.reason || "No automatic gap found -- pick a time manually."}</p>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={r.taken}
                        disabled={!r.id}
                        onCheckedChange={(checked) => r.id && handleToggleTaken(r.id, !!checked)}
                        title={r.id ? "Confirm this break actually happened -- locks it from being auto-pushed" : "Save this break first"}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {stuckShiftIds.has(r.shiftId) ? (
                        <span
                          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-500/20 text-red-300"
                          title="A real booking landed on this break's time and there's no free gap left before the max-hours-before-break cutoff -- needs a manual break time now."
                        >
                          <AlertTriangle className="h-3 w-3" /> Break Overdue -- Assign Now
                        </span>
                      ) : (
                        <span
                          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            r.status === "published"
                              ? "bg-green-500/15 text-green-300"
                              : r.status === "draft"
                              ? "bg-yellow-500/15 text-yellow-300"
                              : "bg-gray-700 text-gray-400"
                          }`}
                        >
                          {r.status === "published" ? "Published" : r.status === "draft" ? "Draft" : "Not saved"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BreaksTab;
